/**
 * Splits a team's schedule into results, upcoming, and the next game.
 *
 * A postponed game is not next-eligible: events-api keeps the stale
 * kickoffAt on it until the provider sends a new date, at which point the
 * status flips back to scheduled. Putting it in the next-game slot would
 * show a date that has already passed. A canceled game is never upcoming:
 * it will not be played. final and canceled are the two finished statuses,
 * matching services/events-api/src/lib/eventStatus.ts's
 * EVENT_STATUS_TRANSITIONS, where those two have no legal transition out.
 *
 * A game events-api no longer serves as live (inProgress or delayed with
 * isLive false, more than 18 hours past kickoff) is treated as finished:
 * it lists with the results under the server's Not final label and is
 * never next (ISSUE-1148).
 *
 * The server returns events oldest first, bounded at 200. This module never
 * re-requests a different order; it reverses in memory when a view needs
 * newest first.
 */
import type { typeTeamScheduleEvent } from "./teamGameLogic";

export const TEAM_GAME_FINISHED_STATUSES = ["final", "canceled"] as const;
export const TEAM_GAME_NEXT_STATUSES = [
  "inProgress",
  "delayed",
  "scheduled",
] as const;

function isFinished(status: typeTeamScheduleEvent["status"]): boolean {
  return (TEAM_GAME_FINISHED_STATUSES as readonly string[]).includes(status);
}

function isNextEligible(status: typeTeamScheduleEvent["status"]): boolean {
  return (TEAM_GAME_NEXT_STATUSES as readonly string[]).includes(status);
}

/**
 * True when events-api serves isLive: false for an inProgress or delayed game,
 * meaning the game is more than 18 hours past kickoff (isServedLive,
 * services/events-api/src/lib/eventStatus.ts). Such a game is treated as
 * finished: it lists with the results under the server's Not final label
 * and is never the next game (ISSUE-1148).
 */
function isServedStale(game: typeTeamScheduleEvent): boolean {
  return (
    (game.status === "inProgress" || game.status === "delayed") && !game.isLive
  );
}

/** Finished games and games no longer served as live, most recent first. */
export function scheduleResults<T extends typeTeamScheduleEvent>(
  games: readonly T[],
): T[] {
  return games
    .filter((game) => isFinished(game.status) || isServedStale(game))
    .sort(
      (a, b) =>
        new Date(b.kickoffAt).getTime() - new Date(a.kickoffAt).getTime(),
    );
}

/** Unfinished games still served as live or not yet started (including postponed), soonest first. */
export function scheduleUpcoming<T extends typeTeamScheduleEvent>(
  games: readonly T[],
): T[] {
  return games
    .filter((game) => !isFinished(game.status) && !isServedStale(game))
    .sort(
      (a, b) =>
        new Date(a.kickoffAt).getTime() - new Date(b.kickoffAt).getTime(),
    );
}

/** The soonest game eligible to be "next", or null. */
export function nextTeamGame<T extends typeTeamScheduleEvent>(
  games: readonly T[],
): T | null {
  const eligible = games
    .filter((game) => isNextEligible(game.status) && !isServedStale(game))
    .sort(
      (a, b) =>
        new Date(a.kickoffAt).getTime() - new Date(b.kickoffAt).getTime(),
    );
  return eligible[0] ?? null;
}

/** The most recent finished games, defaulting to 5. */
export function recentTeamResults<T extends typeTeamScheduleEvent>(
  games: readonly T[],
  limit = 5,
): T[] {
  return scheduleResults(games).slice(0, limit);
}
